import { createSelector, createFeatureSelector } from '@ngrx/store';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import { FiltersState } from './reducer';
import { ILegalEntity } from '@cemex-core/cmx-api';
import { globalFiltersStateKey } from '../models/global-filters-state.interface';
import { GlobalFiltersResult } from '../models/global-filters-result.interfacte';
import { getSelectedBusinessLine } from '@ordersnproducts/modules/order-builder/reducers';
import { ALL_JOBSITES } from '../constants/all-jobsites.constant';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';

export const selectFilterState = createFeatureSelector<FiltersState>(globalFiltersStateKey);

// Get content of the global filter jobsite dropdown, top 5
export const getJobsiteContentSelector = (entity: ILegalEntity, filterState: FiltersState) => {
  if (!entity || !filterState) {
    return [];
  }
  const filterPerCustomer = filterState.filterPerCustomer[entity.legalEntityId];
  if (!filterPerCustomer || !filterPerCustomer.jobsites) {
    return [];
  }
  return filterPerCustomer.jobsites;
};
export const getJobsitesContent = createSelector(getLegalEntity, selectFilterState, getJobsiteContentSelector);

// Get content of all jobsites
export const getAllJobsitesContentSelector = (entity: ILegalEntity, filterState: FiltersState) => {
  if (!entity || !filterState) {
    return [];
  }
  const filterPerCustomer = filterState.filterPerCustomer[entity.legalEntityId];
  if (!filterPerCustomer || !filterPerCustomer.allJobsites) {
    return [];
  }
  return filterPerCustomer.allJobsites;
};
export const getAllJobsitesContent = createSelector(getLegalEntity, selectFilterState, getAllJobsitesContentSelector);

export const getPodsContentSelector = (entity: ILegalEntity, filterState: FiltersState) => {
  if (!entity || !filterState) {
    return [];
  }
  const filterPerCustomer = filterState.filterPerCustomer[entity.legalEntityId];
  if (!filterPerCustomer || !filterPerCustomer.pods) {
    return [];
  }

  return filterPerCustomer.pods;
};

export const getAllPodsContent = createSelector(getLegalEntity, selectFilterState, getPodsContentSelector);

export const getSelectedPodSelector = (entity: ILegalEntity, filterState: FiltersState) => {
  if (!entity || !filterState) {
    return null;
  }
  const filterPerCustomer = filterState.filterPerCustomer[entity.legalEntityId];
  if (!filterPerCustomer || !filterPerCustomer.selectedPodId) {
    return null;
  }

  return filterPerCustomer.selectedPodId;
};
export const getSelectedPod = createSelector(getLegalEntity, selectFilterState, getSelectedPodSelector);

export const getPodLoaderSelector = (filterState: FiltersState) => filterState.podLoader;
export const getPodLoader = createSelector(selectFilterState, getPodLoaderSelector);

// Get selected jobsite id for global filter funcionality
export const getSelectedJobsiteIdSelector = (entity: ILegalEntity, filterState: FiltersState) => {
  if (!entity || !filterState) {
    return null;
  }
  const filterPerCustomer = filterState.filterPerCustomer[entity.legalEntityId];
  if (!filterPerCustomer || !filterPerCustomer.selectedJobsiteId) {
    return null;
  }
  return filterPerCustomer.selectedJobsiteId;
};
export const getSelectedJobsiteId = createSelector(getLegalEntity, selectFilterState, getSelectedJobsiteIdSelector);

export const getJobsiteFilterSelector = (filterState: FiltersState) => filterState.jobsiteFilter;
export const getJobsiteFilter = createSelector(selectFilterState, getJobsiteFilterSelector);

export const getJobisteLoaderSelector = (filterState: FiltersState) => filterState.jobsiteLoader;
export const getSelectingJobisteSelector = (filterState: FiltersState) => filterState.selectingJobsite;

export const getJobisteLoader = createSelector(selectFilterState, getJobisteLoaderSelector);
export const getSelectingJobiste = createSelector(selectFilterState, getSelectingJobisteSelector);

/*********************************************
 * Product Lines Selectors
 * *******************************************/

// Get ids of active product lines for jobsite
export const getActiveProductLinesSelector = (filtersState: FiltersState, jobsiteId: number) => {
  const activePrudctLinesPerJobsite = filtersState.activeProductLinesIdsPerJobsite[jobsiteId];
  if (!jobsiteId) {
    return null;
  }
  if (!activePrudctLinesPerJobsite || !activePrudctLinesPerJobsite.length) {
    return [];
  }
  return activePrudctLinesPerJobsite;
};
export const getActiveProductLines = createSelector(
  selectFilterState,
  getSelectedJobsiteId,
  getActiveProductLinesSelector,
);

// Get content of the global filter productline dropdown
export const getProductLinesContentSelector = (
  entity: ILegalEntity,
  filterState: FiltersState,
  enabledProductLinesIds: number[],
) => {
  if (!entity || !filterState) {
    return [];
  }
  const filterPerCustomer = filterState.filterPerCustomer[entity.legalEntityId];
  if (!filterPerCustomer || !filterPerCustomer.productLines) {
    return [];
  }
  if (!enabledProductLinesIds) {
    return filterPerCustomer.productLines;
  }

  return filterPerCustomer.productLines.map((productLine) => ({
    ...productLine,
    disabled: enabledProductLinesIds.indexOf(productLine.id) < 0,
  }));
};
export const getProductLinesContent = createSelector(
  getLegalEntity,
  selectFilterState,
  getActiveProductLines,
  getProductLinesContentSelector,
);

// Get array of selected product lines ids
export const getSelectedProductLinesIdsSelector = (entity: ILegalEntity, filterState: FiltersState) => {
  if (!entity || !filterState) {
    return [];
  }
  const filterPerCustomer = filterState.filterPerCustomer[entity.legalEntityId];
  if (!filterPerCustomer || !filterPerCustomer.selectedProductLinesIds) {
    return [];
  }
  return filterPerCustomer.selectedProductLinesIds;
};
export const getSelectedProductLinesIds = createSelector(
  getLegalEntity,
  selectFilterState,
  getSelectedProductLinesIdsSelector,
);

export const getProductLineLoaderSelector = (filterState: FiltersState) => filterState.productLineLoader;
export const getProductLineLoader = createSelector(selectFilterState, getProductLineLoaderSelector);

/***********************************************************************************************
 * Public Selectors
 ***********************************************************************************************/

export const getSelectedJobsiteSelector = (entity: ILegalEntity, filterState: FiltersState) => {
  if (!entity || !filterState) {
    return null;
  }
  const filterPerCustomer = filterState.filterPerCustomer[entity.legalEntityId];
  if (!filterPerCustomer || !filterPerCustomer.selectedJobsiteId) {
    return null;
  }
  const selectedJobsiteId = filterPerCustomer.selectedJobsiteId;
  const jobsites = filterPerCustomer.allJobsites;
  if (!Array.isArray(jobsites) || jobsites.length === 0) {
    return null;
  }

  if (selectedJobsiteId === ALL_JOBSITES) {
    return {
      code: '1',
      id: 1,
      name: 'All Jobsites',
    };
  } else {
    const jobsite = jobsites.find((item) => item.id === selectedJobsiteId) || null;
    return jobsite;
  }
};
export const getSelectedJobsite = createSelector(getLegalEntity, selectFilterState, getSelectedJobsiteSelector);

export const getSelectedProductLinesSelector = (entity: ILegalEntity, filterState: FiltersState) => {
  if (!entity || !filterState) {
    return null;
  }
  const filterPerCustomer = filterState.filterPerCustomer[entity.legalEntityId];
  if (!filterPerCustomer || !filterPerCustomer.selectedProductLinesIds) {
    return null;
  }
  const selectedProductLinesIds = filterPerCustomer.selectedProductLinesIds;
  if (!Array.isArray(selectedProductLinesIds) || selectedProductLinesIds.length === 0) {
    return null;
  }
  const productLines = filterPerCustomer.productLines;
  if (!Array.isArray(productLines) || productLines.length === 0) {
    return null;
  }
  const selectedProductLines = selectedProductLinesIds
    .map((id) => {
      const productLine = productLines.find((item) => item.id === id);
      return productLine ? productLine : null;
    })
    .filter((item) => item !== null);
  if (selectedProductLines.length === 0) {
    return null;
  }
  return selectedProductLines;
};
export const getSelectedProductLines = createSelector(
  getLegalEntity,
  selectFilterState,
  getSelectedProductLinesSelector,
);

export const getGlobalFilters: (args: any) => GlobalFiltersResult = createSelector(
  getSelectedJobsite,
  getSelectedPod,
  getSelectedProductLines,
  (jobsite, podId, productLines) => {
    productLines = productLines || [];
    return { jobsite, podId, productLines };
  },
);

export const getGlobalFilterAndOrderTaking = createSelector(
  getGlobalFilters,
  getSelectedBusinessLine,
  (globalFilter, orderTaking) => {
    return {
      globalFilterJobsite: _safe(() => globalFilter.jobsite),
      selectedSameProdLine: _safe(() =>
        globalFilter.productLines.some((prodLine) => prodLine.id === +orderTaking.productTypeId),
      ),
    };
  },
);
